import { useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { 
  Award, Star, MessageCircle, Briefcase, GraduationCap, 
  Shield, Target, Zap, Users, ArrowRight, MapPin, Calendar
} from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";

const WHATSAPP_NUMBER = "33644692901";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Bonjour ! Je souhaite en savoir plus sur vos formations IA.")}`;

const FormateursPage = () => {
  const [activeTrainer, setActiveTrainer] = useState(null);

  const trainers = [
    {
      id: "jbg",
      initials: "JBG",
      name: "Jean-Baptiste GIRAUD",
      role: "Fondateur de l'Académie IA",
      title: "Expert IA • Serial Entrepreneur • Journaliste High-Tech",
      photo: "https://customer-assets.emergentagent.com/job_01e865c2-013e-4444-9089-dd388040a505/artifacts/eucy2b90_IMG_3268.jpeg",
      color: "cyan",
      gradient: "from-cyan-500 to-blue-600",
      bgGradient: "from-cyan-900/30 to-blue-900/30",
      borderColor: "border-cyan-500/30",
      textColor: "text-cyan-400",
      tagBg: "bg-cyan-500/10",
      tagBorder: "border-cyan-500/30",
      badges: ["Fondateur", "20 ans XP", "N°1 App Store"],
      shortBio: "Pionnier du mobile, formateur des dirigeants du SBF 120 et expert reconnu de l'IA en France.",
      fullBio: [
        "Journaliste high-tech, serial entrepreneur, ancien de BFM, TF1 et RTL. 20 ans de formation et plusieurs milliers de personnes formées à son actif, dont des patrons du SBF 120, des ministres, des parlementaires et des très hauts gradés.",
        "Formateur expert IA depuis 2023, ancré dans le réel au quotidien. Jean-Baptiste a développé sa première application mobile en 2006, avant l'arrivée de l'iPhone, pour le Nokia N70.",
        "Numéro 1 de l'App Store dès 2010 pendant 2 mois, il a depuis développé plus d'une centaine d'applications métier utilisées par les plus grands groupes et institutions : Ouest France, Bolloré Media, Canal+, France Télévisions, Marine Nationale... et bien entendu des dizaines d'ETI ou PME."
      ],
      vision: "Un problème, un besoin = une app dédiée ou un portfolio d'apps. Je développe entre 3 et 5 apps par semaine avec le vibe coding tout en faisant évoluer celles créées les semaines précédentes. L'IA et le vibe coding me font vivre l'un des moments les plus excitants de ma vie.",
      skills: ["Vibe Coding", "Emergent", "Apps Métier", "Formation COMEX", "IA Générative", "Stratégie Digitale"],
      stats: [
        { label: "Personnes formées", value: "5000+" },
        { label: "Apps développées", value: "100+" },
        { label: "Années d'expérience", value: "20+" },
        { label: "Apps/semaine", value: "3-5" }
      ],
      clients: ["SBF 120", "Ministères", "Canal+", "France TV", "Marine Nationale", "Ouest France"]
    },
    {
      id: "ta",
      initials: "TA",
      name: "Thierry ASSONION",
      role: "Fondateur du cabinet Anticiper",
      title: "Expert Renseignement • Officier Supérieur • Breveté École de Guerre",
      photo: "https://customer-assets.emergentagent.com/job_01e865c2-013e-4444-9089-dd388040a505/artifacts/nagb2cvz_0871139a-c59e-4242-88b5-1c9dfc36e39d.jpeg",
      color: "purple",
      gradient: "from-purple-500 to-pink-600",
      bgGradient: "from-purple-900/30 to-pink-900/30",
      borderColor: "border-purple-500/30",
      textColor: "text-purple-400",
      tagBg: "bg-purple-500/10",
      tagBorder: "border-purple-500/30",
      badges: ["École de Guerre", "INRIA", "Renseignement"],
      shortBio: "Ancien officier supérieur, expert du renseignement et concepteur de plateformes d'analyse stratégique IA.",
      fullBio: [
        "Ancien officier supérieur de l'Armée de Terre, breveté de l'École de Guerre, expert du renseignement, Thierry Assonion est le fondateur du cabinet de conseil 'Anticiper'.",
        "Depuis 2020, il apporte son expertise en matière de traitement et d'analyse de l'information à des entreprises et des établissements de recherche comme l'INRIA ou l'Université Technologique de Compiègne.",
        "Concepteur d'une plateforme d'analyse de l'information stratégique embarquant des agents IA, il conçoit également des formations à la coopération inter-fonctions et à l'interprétation des signaux faibles précurseurs de risques ou d'opportunités."
      ],
      vision: "Je développe des war games et des simulations ainsi que des jeux de société, désormais tous assistés ou conçus par l'IA, que je déploie chez mes clients à des fins de formation continue et d'entraînement. Je suis spécialiste de la gestion des données et des risques.",
      skills: ["Intelligence Stratégique", "Agents IA", "War Games", "Signaux Faibles", "OSINT", "Gestion des Risques"],
      stats: [
        { label: "Années renseignement", value: "25+" },
        { label: "Simulations créées", value: "50+" },
        { label: "Entreprises accompagnées", value: "30+" },
        { label: "Brevets & diplômes", value: "5+" }
      ],
      clients: ["INRIA", "UTC", "Grandes Entreprises", "Défense", "Recherche"]
    },
    {
      id: "tm",
      initials: "TM",
      name: "Thibaut MILEWSKI",
      role: "Expert Simulation & OSINT",
      title: "Spécialiste Données • Collaborateur Anticiper • Développeur IA",
      photo: "https://customer-assets.emergentagent.com/job_01e865c2-013e-4444-9089-dd388040a505/artifacts/ec798rh2_668f3d1a-e302-4ad9-8aad-8c15a24d1c65.jpeg",
      color: "amber",
      gradient: "from-amber-500 to-orange-600",
      bgGradient: "from-amber-900/30 to-orange-900/30",
      borderColor: "border-amber-500/30",
      textColor: "text-amber-400",
      tagBg: "bg-amber-500/10",
      tagBorder: "border-amber-500/30",
      badges: ["OSINT", "Simulation", "IA depuis 2023"],
      shortBio: "Expert OSINT et simulation, il développe des outils IA et anime les formations avec Anticiper.",
      fullBio: [
        "Spécialiste des dispositifs de simulation et d'exploitation de données représentatives du renseignement, Thibaut Milewski collabore avec Anticiper depuis 2021, notamment pour l'élaboration et l'animation des formations.",
        "Expert OSINT (Open Source Intelligence), il participe à la collecte et l'analyse d'informations dans le cadre de l'appui opérationnel des entreprises par les équipes d'Anticiper.",
        "Il développe et configure des outils IA depuis 2023, apportant une expertise technique précieuse aux projets de formation et d'analyse."
      ],
      vision: "L'OSINT et l'IA sont des outils complémentaires extraordinaires. Je m'attache à rendre accessible l'analyse de données complexes grâce à des interfaces intelligentes et des méthodologies éprouvées.",
      skills: ["OSINT", "Simulation", "Analyse de Données", "Configuration IA", "Animation Formation", "Collecte Info"],
      stats: [
        { label: "Projets OSINT", value: "40+" },
        { label: "Formations animées", value: "100+" },
        { label: "Outils IA configurés", value: "20+" },
        { label: "Années avec Anticiper", value: "4+" }
      ],
      clients: ["Anticiper", "Entreprises", "Institutions"]
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="formateurs-page">
      <Helmet>
        <title>Nos Formateurs IA Experts | Académie IA</title>
        <meta name="description" content="Découvrez nos formateurs experts en Intelligence Artificielle : Jean-Baptiste Giraud (fondateur), Thierry Assonion (renseignement), Thibaut Milewski (OSINT). Des praticiens qui déploient l'IA au quotidien." />
      </Helmet>

      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-900/20 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium mb-8">
              <Star className="w-4 h-4" />
              L'équipe qui va transformer votre entreprise
            </span>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Nos <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Formateurs</span>
            </h1>
            
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Ce ne sont pas des formateurs théoriques. Ce sont des <strong className="text-cyan-400">praticiens experts</strong> qui 
              déploient l'IA au quotidien dans les plus grandes entreprises françaises.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full text-gray-300">
                <Award className="w-5 h-5 text-cyan-400" />
                <span>5000+ personnes formées</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full text-gray-300">
                <Briefcase className="w-5 h-5 text-purple-400" />
                <span>SBF 120, Ministères, Armée</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full text-gray-300">
                <Zap className="w-5 h-5 text-amber-400" />
                <span>100+ apps créées avec l'IA</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trainers Grid */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto space-y-12">
          {trainers.map((trainer, index) => (
            <motion.div
              key={trainer.id}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className={`relative bg-gradient-to-r ${trainer.bgGradient} border ${trainer.borderColor} rounded-3xl p-8 overflow-hidden`}
              data-testid={`trainer-card-${trainer.id}`}
            >
              {/* Background glow */}
              <div className={`absolute ${index % 2 === 0 ? 'top-0 right-0' : 'bottom-0 left-0'} w-64 h-64 bg-${trainer.color}-500/10 rounded-full blur-3xl`} />
              
              <div className={`relative flex flex-col lg:flex-row ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''} gap-8 items-start`}>
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {trainer.photo ? (
                    <img 
                      src={trainer.photo} 
                      alt={trainer.name}
                      className="w-32 h-32 lg:w-44 lg:h-44 rounded-2xl object-cover shadow-xl"
                    />
                  ) : (
                    <div className={`w-32 h-32 lg:w-44 lg:h-44 rounded-2xl bg-gradient-to-br ${trainer.gradient} flex items-center justify-center text-white text-5xl lg:text-6xl font-bold shadow-xl`}>
                      {trainer.initials}
                    </div>
                  )}
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {trainer.badges.map((badge) => (
                      <span key={badge} className={`px-3 py-1 ${trainer.tagBg} border ${trainer.tagBorder} rounded-full ${trainer.textColor} text-xs font-semibold`}>
                        {badge}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-1">{trainer.name}</h2>
                  <p className={`${trainer.textColor} font-semibold mb-2`}>{trainer.role}</p>
                  <p className="text-gray-400 text-sm mb-4">{trainer.title}</p>
                  
                  {/* Bio */}
                  <div className="space-y-3 mb-6">
                    {trainer.fullBio.map((paragraph, i) => (
                      <p key={i} className="text-gray-300 leading-relaxed">{paragraph}</p>
                    ))}
                  </div>

                  {/* Vision Quote */}
                  <div className={`bg-black/30 rounded-xl p-4 border ${trainer.tagBorder} mb-6`}>
                    <p className="text-white italic">
                      "{trainer.vision}"
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {trainer.stats.map((stat) => (
                      <div key={stat.label} className="text-center">
                        <div className={`text-2xl font-bold ${trainer.textColor}`}>{stat.value}</div>
                        <div className="text-gray-500 text-sm">{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Skills */}
                  <div className="flex flex-wrap gap-2">
                    {trainer.skills.map((skill) => (
                      <span key={skill} className={`px-3 py-1 ${trainer.tagBg} border ${trainer.tagBorder} rounded-full ${trainer.textColor} text-sm`}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#050505] to-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Ce Qui Nous Différencie
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Pas de slides PowerPoint soporifiques. Que du <span className="text-cyan-400">concret</span> et de <span className="text-cyan-400">l'action</span>.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Expérience Terrain",
                desc: "Nos formateurs ont formé des ministres, des généraux, des PDG du SBF 120. Ils savent adapter leur discours à tous les niveaux.",
                color: "cyan"
              },
              {
                icon: Zap,
                title: "100% Pratique",
                desc: "Chaque participant repart avec ses propres outils IA configurés. On créé, on teste, on déploie pendant la formation.",
                color: "purple"
              },
              {
                icon: Target,
                title: "ROI Immédiat",
                desc: "Dès le lendemain, vos équipes sont opérationnelles. Gains de temps mesurables en heures/semaine.",
                color: "amber"
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center hover:border-white/20 transition-all"
              >
                <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-${item.color}-500/10 flex items-center justify-center`}>
                  <item.icon className={`w-8 h-8 text-${item.color}-400`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-cyan-900/50 to-blue-900/50 border border-cyan-500/30 rounded-3xl p-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à Former Vos Équipes ?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Contactez-nous pour organiser une formation IA sur mesure dans votre entreprise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/tunnel"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold rounded-full transition-all shadow-lg shadow-cyan-500/30"
                data-testid="cta-devis"
              >
                <Zap className="w-5 h-5" />
                Demander un Devis Gratuit
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-full transition-all"
                data-testid="cta-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                Contacter sur WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-white/10 bg-black/50">
        <p className="text-gray-500 text-sm">
          Cette page a été <span className="text-cyan-400 font-semibold">entièrement générée par l'IA</span> avec{" "}
          <a 
            href="https://emergent.sh" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-cyan-400 hover:text-cyan-300 underline"
          >
            Emergent
          </a>
        </p>
        <p className="text-gray-600 text-xs mt-2">
          Académie IA © {new Date().getFullYear()} — Formation Intelligence Artificielle
        </p>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-6 py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-full shadow-lg shadow-green-500/30 hover:scale-110 transition-all"
        data-testid="floating-whatsapp"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="hidden sm:inline">Contactez-nous</span>
      </a>
    </div>
  );
};

export default FormateursPage;
